import {
    api,
    apiErrorMessage,
    listSpec,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Checkbox,
    ConfirmDialog,
    DateTime,
    KeyValueList,
    Label,
    Money,
    ObjectRef,
    PageHeader,
    PublicId,
    StatusBadge,
    statusLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';

import type {
    StaffAttempt,
    StaffPeriod,
    StaffSubscription,
    Subscription,
} from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import {
    subscriptionSubjects,
    WebhookDeliveries,
} from '@/components/webhook-deliveries';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';
import { useMe, useStaffDirectory, whoName } from '@/lib/staff';
import { Section } from '@/pages/orders/detail';
import { AmountEvery } from '@/pages/subscriptions/index';

// ponytail: the newest 100 periods (the route's page ceiling); page the table when a subscription renews more often
const PERIODS = { sort: '-number', per_page: 100 } as const;

/** The reads of `/subscriptions/:id` (BM-46 §2.0): the staff view (§ 6) and its periods, newest first. */
export const subscriptionReads = (id: string | undefined) => ({
    subscription: oneSpec(`/staff/subscriptions/${id ?? ''}`),
    periods: listSpec(`/staff/subscriptions/${id ?? ''}/periods`, PERIODS),
});

type Action = 'cancel' | 'retry' | 'cardLink';
type Reason = StaffSubscription['status_reason'];
type RetryResult = { result?: string; subscription?: Subscription };

/** One line saying why the subscription is past due or cancelled (§ 6); null when it is neither. */
function reasonLine(s: StaffSubscription): string | null {
    const r = s.status_reason;
    if (
        r.cancel_source &&
        (s.status === 'cancelled' || r.cancel_at_period_end)
    ) {
        const by = statusLabel(r.cancel_source).toLowerCase();
        const head =
            s.status === 'cancelled'
                ? `Cancelled by ${by}`
                : `Cancels at period end, by ${by}`;
        return r.cancel_reason ? `${head}: ${r.cancel_reason}` : head;
    }
    if (r.past_due_since) {
        const retry =
            r.attempts === null
                ? null
                : `retry ${r.attempts}${r.max_attempts === null ? '' : ` of ${r.max_attempts}`}`;
        return ['Past due: ' + (r.last_decline_category ?? 'declined'), retry]
            .filter(Boolean)
            .join(' · ');
    }
    return null;
}

const retries = (r: Reason) =>
    r.attempts === null
        ? '—'
        : r.max_attempts === null
          ? String(r.attempts)
          : `${r.attempts} of ${r.max_attempts}`;

// read beside the subscription, not under it, so the route's prefetch seeds both (BM-46 §2.0)
const usePeriods = (id: string) =>
    useList<StaffPeriod>(`/staff/subscriptions/${id}/periods`, PERIODS);

/** The periods, newest first; each links its order and charge (D-3, D-5) and expands to its attempts (D-19). */
function Periods({
    list,
    currency,
}: {
    list: ReturnType<typeof usePeriods>;
    currency: string;
}) {
    const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
    const toggle = (pid: string) =>
        setOpen((o) => {
            const next = new Set(o);
            if (!next.delete(pid)) next.add(pid);
            return next;
        });
    const rows = list.data?.data.items ?? [];
    const money = (cents: number | undefined) =>
        cents === undefined ? '—' : <Money cents={cents} currency={currency} />;
    return (
        <Section title="Periods" testId="subscription-periods">
            {list.error ? (
                <p role="alert" className="text-destructive text-sm">
                    {apiErrorMessage(list.error)}
                </p>
            ) : list.isPending ? null : rows.length === 0 ? (
                <p className="text-muted-foreground text-sm">No periods yet.</p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Period</TableHead>
                                <TableHead>Dates</TableHead>
                                <TableHead>Due</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead>Total</TableHead>
                                <TableHead>Order</TableHead>
                                <TableHead>Charge</TableHead>
                                <TableHead>Attempts</TableHead>
                                <TableHead>Paid</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((p) => {
                                // StaffPeriod is Period allOf { attempts_log: StaffAttempt[] }: the intersection of
                                // the two array types indexes as the base Attempt, so name the staff row type
                                const log: readonly StaffAttempt[] =
                                    p.attempts_log ?? [];
                                const expanded = open.has(p.id);
                                // a declined period has no order and no charge of its own: its last attempt's charge
                                // stands in, with the decline (D-3, § 5's period table)
                                const charge =
                                    p.charge_id ??
                                    log.at(-1)?.charge_id ??
                                    null;
                                return (
                                    <Fragment key={p.id}>
                                        <TableRow
                                            data-testid="period-row"
                                            data-number={p.number}
                                        >
                                            <TableCell className="whitespace-nowrap">
                                                {log.length > 0 ? (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-expanded={expanded}
                                                        aria-label={`Attempts of period ${p.number}`}
                                                        onClick={() =>
                                                            toggle(p.id)
                                                        }
                                                    >
                                                        {expanded ? '▾' : '▸'} #
                                                        {p.number}
                                                    </Button>
                                                ) : (
                                                    <span className="px-3">
                                                        #{p.number}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                <DateTime
                                                    value={p.period_start}
                                                    format="date"
                                                />{' '}
                                                –{' '}
                                                <DateTime
                                                    value={p.period_end}
                                                    format="date"
                                                />
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                <DateTime value={p.due_at} />
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    status={p.state}
                                                    domain="period"
                                                />
                                            </TableCell>
                                            <TableCell className="tabular-nums">
                                                {money(
                                                    p.total_cents ??
                                                        p.amount_cents,
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {p.order_id ? (
                                                    <ObjectRef
                                                        id={p.order_id}
                                                    />
                                                ) : (
                                                    '—'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {charge ? (
                                                    <ObjectRef id={charge} />
                                                ) : (
                                                    '—'
                                                )}
                                                {p.state === 'declined' &&
                                                p.last_decline_category ? (
                                                    <span className="text-muted-foreground ml-2 text-xs">
                                                        {
                                                            p.last_decline_category
                                                        }
                                                    </span>
                                                ) : null}
                                            </TableCell>
                                            <TableCell className="tabular-nums">
                                                {p.attempts ?? 0}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                <DateTime
                                                    value={p.paid_at}
                                                    empty="—"
                                                />
                                            </TableCell>
                                        </TableRow>
                                        {expanded
                                            ? log.map((a) => (
                                                  <TableRow
                                                      key={`${p.id}-${a.attempt}`}
                                                      data-testid="attempt-row"
                                                      className="bg-muted/40"
                                                  >
                                                      <TableCell className="pl-8 text-xs">
                                                          Attempt {a.attempt}
                                                      </TableCell>
                                                      <TableCell className="text-xs whitespace-nowrap">
                                                          <DateTime
                                                              value={
                                                                  a.started_at ??
                                                                  a.scheduled_at
                                                              }
                                                              empty="—"
                                                          />
                                                      </TableCell>
                                                      <TableCell className="text-xs whitespace-nowrap">
                                                          <DateTime
                                                              value={
                                                                  a.finished_at
                                                              }
                                                              empty="—"
                                                          />
                                                      </TableCell>
                                                      <TableCell>
                                                          {a.result ? (
                                                              <StatusBadge
                                                                  status={
                                                                      a.result
                                                                  }
                                                              />
                                                          ) : (
                                                              '—'
                                                          )}
                                                      </TableCell>
                                                      <TableCell className="text-xs">
                                                          {a.decline_category ??
                                                              a.skip_reason ??
                                                              '—'}
                                                      </TableCell>
                                                      <TableCell />
                                                      <TableCell>
                                                          {a.charge_id ? (
                                                              <ObjectRef
                                                                  id={
                                                                      a.charge_id
                                                                  }
                                                              />
                                                          ) : (
                                                              '—'
                                                          )}
                                                      </TableCell>
                                                      <TableCell />
                                                      <TableCell />
                                                  </TableRow>
                                              ))
                                            : null}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </Section>
    );
}

function SubscriptionBody({
    s,
    periods,
}: {
    s: StaffSubscription;
    periods: ReturnType<typeof usePeriods>;
}) {
    const me = useMe();
    const directory = useStaffDirectory();
    const r = s.status_reason;
    const cancelled =
        s.status === 'cancelled' || r.cancel_at_period_end || !!r.cancel_source;
    // bm-147 § 8 (K-2): built from the periods the page already read; the subscription alone if they failed
    const rolled = periods.isPending
        ? undefined
        : subscriptionSubjects(s.id, periods.data?.data.items ?? []);
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Section title="Plan" testId="subscription-plan">
                    <KeyValueList
                        items={[
                            {
                                label: 'Status',
                                value: (
                                    <StatusBadge
                                        status={s.status}
                                        domain="subscription"
                                    />
                                ),
                            },
                            {
                                label: 'Plan',
                                value: <PublicId id={s.plan_id} />,
                            },
                            s.price_id
                                ? {
                                      label: 'Price',
                                      value: <PublicId id={s.price_id} />,
                                  }
                                : null,
                            {
                                label: 'Amount',
                                value: <AmountEvery s={s} />,
                                num: true,
                            },
                            {
                                label: 'Trial days',
                                value: s.plan?.trial_days ?? 0,
                                num: true,
                            },
                            {
                                label: 'Trial ends',
                                value: (
                                    <DateTime value={s.trial_end} empty="—" />
                                ),
                            },
                            {
                                label: 'Current period',
                                value: (
                                    <>
                                        <DateTime
                                            value={s.current_period_start}
                                            format="date"
                                        />{' '}
                                        –{' '}
                                        <DateTime
                                            value={s.current_period_end}
                                            format="date"
                                        />
                                    </>
                                ),
                            },
                            {
                                label: 'Next renewal',
                                value: (
                                    <DateTime
                                        value={s.next_charge_at}
                                        empty="—"
                                    />
                                ),
                            },
                            s.pause_at
                                ? {
                                      label: 'Pauses on',
                                      value: <DateTime value={s.pause_at} />,
                                  }
                                : null,
                            {
                                label: 'Billing mode',
                                value: statusLabel(s.billing_mode ?? ''),
                            },
                            {
                                label: 'Created',
                                value: (
                                    <DateTime value={s.created_at} empty="—" />
                                ),
                            },
                        ]}
                    />
                </Section>
                <Section title="Links" testId="subscription-links">
                    <KeyValueList
                        items={[
                            {
                                label: 'Merchant',
                                value: <ObjectRef id={s.merchant_id} />,
                            },
                            // D-13: the store is the subscription's, shown once; periods carry none
                            s.store_id
                                ? {
                                      label: 'Store',
                                      value: (
                                          <ObjectRef
                                              id={s.store_id}
                                              label={
                                                  s.store?.display_name ??
                                                  s.store?.name
                                              }
                                              href={`/stores/${encodeURIComponent(s.store_id)}`}
                                          />
                                      ),
                                  }
                                : null,
                            { label: 'Buyer', value: s.buyer_email },
                            {
                                label: 'Started from',
                                value: s.origin_order_id ? (
                                    <ObjectRef id={s.origin_order_id} />
                                ) : (
                                    '—'
                                ),
                            },
                            {
                                label: 'Payment method',
                                value: s.payment_method_id ? (
                                    <ObjectRef id={s.payment_method_id} />
                                ) : (
                                    '—'
                                ),
                            },
                        ]}
                    />
                </Section>
                {r.past_due_since ? (
                    <Section title="Past due" testId="past-due">
                        <KeyValueList
                            items={[
                                {
                                    label: 'Past due since',
                                    value: (
                                        <DateTime value={r.past_due_since} />
                                    ),
                                },
                                {
                                    label: 'Last decline',
                                    value: r.last_decline_category ?? '—',
                                },
                                {
                                    label: 'Retries',
                                    value: retries(r),
                                    num: true,
                                },
                                {
                                    label: 'Next attempt',
                                    value: (
                                        <DateTime
                                            value={r.next_attempt_at}
                                            empty="—"
                                        />
                                    ),
                                },
                                {
                                    label: 'Grace ends',
                                    value: (
                                        <DateTime
                                            value={r.grace_ends_at}
                                            empty="—"
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Section>
                ) : null}
                {cancelled ? (
                    <Section title="Cancellation" testId="cancellation">
                        <KeyValueList
                            items={[
                                {
                                    label: 'Source',
                                    value: r.cancel_source
                                        ? statusLabel(r.cancel_source)
                                        : '—',
                                },
                                {
                                    label: 'Reason',
                                    value: r.cancel_reason ?? '—',
                                },
                                {
                                    label: 'Cancelled by',
                                    value: r.cancel_actor
                                        ? `${whoName(r.cancel_actor.id, me, directory)}${r.cancel_actor.type ? ` (${r.cancel_actor.type})` : ''}`
                                        : '—',
                                },
                                {
                                    label: 'Cancelled',
                                    value: (
                                        <DateTime
                                            value={r.cancelled_at}
                                            empty="—"
                                        />
                                    ),
                                },
                                {
                                    label: 'Ends at period end',
                                    value: r.cancel_at_period_end
                                        ? 'Yes'
                                        : 'No',
                                },
                                {
                                    label: 'Ended',
                                    value: (
                                        <DateTime
                                            value={r.ended_at}
                                            empty="Not yet"
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Section>
                ) : null}
            </div>
            <Periods list={periods} currency={s.currency} />
            {/* D-24: the status timeline, pause and resume history and card-update links sent are its audit rows */}
            <ActivityCard entity="subscription" id={s.id} />
            <WebhookDeliveries
                subjects={rolled?.subjects}
                notice={
                    rolled?.truncated
                        ? 'Deliveries of older periods are not shown.'
                        : undefined
                }
            />
        </div>
    );
}

/**
 * bm-147 § 5 (US-005): one subscription as staff read it (§ 6's staff view): the status reason, plan and trial, the
 * past-due and cancellation blocks, the periods newest first with their attempts, the Activity panel (the status
 * timeline, D-24) and the deliveries panel. Cancel, retry and send card-update link need billing.subscriptions.manage
 * and a subscription that is not cancelled; each refusal shows the API's message inside its dialog.
 */
export function SubscriptionPage() {
    const { id = '' } = useParams();
    const can = useAuthStore((s) => s.can);
    const query = useOne<StaffSubscription>(
        subscriptionReads(id).subscription.path,
    );
    const periods = usePeriods(id);
    const [open, setOpen] = useState<Action | null>(null);
    const [immediately, setImmediately] = useState(false);
    const invalidate = [`/staff/subscriptions/${id}`, '/staff/audit'];
    const post = <T,>(action: string, body?: unknown) =>
        api()
            .post<T>(
                `/staff/subscriptions/${encodeURIComponent(id)}/${action}`,
                body,
            )
            .then((r) => r.data);
    const cancel = useWrite(
        (body: { immediately: boolean; reason?: string }) =>
            post<Subscription>('cancel', body),
        invalidate,
    );
    const retry = useWrite(() => post<RetryResult>('retry'), invalidate);
    const cardLink = useWrite(
        () => post<Subscription>('card-link'),
        invalidate,
    );
    const writes = { cancel, retry, cardLink };
    const show = (action: Action | null) => {
        writes.cancel.reset();
        writes.retry.reset();
        writes.cardLink.reset();
        setImmediately(false);
        setOpen(action);
    };
    const close = { onSuccess: () => setOpen(null) };
    const error = (a: Action) =>
        writes[a].error ? apiErrorMessage(writes[a].error) : undefined;
    const s = query.data?.data;
    const manages =
        s !== undefined &&
        can(P.subscriptionsManage) &&
        s.status !== 'cancelled';
    const reason = s ? reasonLine(s) : null;
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Subscriptions', href: '/subscriptions' }]}
                {...detailTitle(query, null, id, 'Subscription')}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
                primaryAction={
                    manages ? (
                        <Button
                            variant="destructive"
                            onClick={() => show('cancel')}
                        >
                            Cancel
                        </Button>
                    ) : null
                }
                secondaryAction={
                    manages ? (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => show('retry')}
                            >
                                Retry now
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => show('cardLink')}
                            >
                                Send card-update link
                            </Button>
                        </>
                    ) : null
                }
            />
            {reason ? (
                <p
                    className="mb-4 text-sm font-medium"
                    data-testid="status-reason"
                >
                    {reason}
                </p>
            ) : null}
            {retry.data?.result ? (
                <p className="mb-4 text-sm" data-testid="retry-result">
                    Retry result: {statusLabel(retry.data.result)}
                </p>
            ) : null}
            <DetailBody query={query} title="Subscription">
                {(sub) => <SubscriptionBody s={sub} periods={periods} />}
            </DetailBody>
            <ConfirmDialog
                open={open === 'cancel'}
                onOpenChange={(o) => (o ? null : setOpen(null))}
                title="Cancel this subscription?"
                description="Future billing stops at the end of the paid period, unless you cancel immediately."
                confirmLabel="Cancel subscription"
                destructive
                reason={{ label: 'Reason', required: false }}
                busy={cancel.isPending}
                error={error('cancel')}
                onConfirm={({ reason: why }) =>
                    cancel.mutate(
                        { immediately, reason: why.trim() || undefined },
                        close,
                    )
                }
            >
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="cancel-immediately"
                        checked={immediately}
                        onCheckedChange={(v) => setImmediately(v === true)}
                    />
                    <Label htmlFor="cancel-immediately">
                        Cancel immediately (end access now)
                    </Label>
                </div>
            </ConfirmDialog>
            <ConfirmDialog
                open={open === 'retry'}
                onOpenChange={(o) => (o ? null : setOpen(null))}
                title="Retry the due period now?"
                description="The saved card is charged now for the declined or due period."
                confirmLabel="Retry"
                busy={retry.isPending}
                error={error('retry')}
                onConfirm={() => retry.mutate(undefined, close)}
            />
            <ConfirmDialog
                open={open === 'cardLink'}
                onOpenChange={(o) => (o ? null : setOpen(null))}
                title="Send a card-update link?"
                description={`The buyer gets an email at ${s?.buyer_email ?? ''} with a link to update the card for this subscription.`}
                confirmLabel="Send link"
                busy={cardLink.isPending}
                error={error('cardLink')}
                onConfirm={() => cardLink.mutate(undefined, close)}
            />
        </div>
    );
}
